/**
 * @returns {CryptoKey} Random bytes that can be used as initial vector for AES cipher
 */
async function generateAesKey (): Promise<CryptoKey> {
  const aesAlgoKeyGen: AesKeyGenParams = {
    name: 'AES-CBC',
    length: 256,
  }

  return window.crypto.subtle.generateKey(aesAlgoKeyGen, true, ['encrypt', 'decrypt']) as Promise<CryptoKey>
}

/**
 * @returns {Uint8Array} Random bytes that can be used as initial vector for AES cipher
 */
function generateAesIV (): Uint8Array {
  return window.crypto.getRandomValues(new Uint8Array(16))
}

function stringToBuffer (message: string): ArrayBuffer {
  const encoder = new TextEncoder()
  return encoder.encode(message).buffer
}

/**
 * @param {ArrayBuffer} messageBuffer Message converted to buffer
 * @param {CryptoKey} aesKey AES key
 * @param {Uint8Array} iv AES initial vector
 * @returns {ArrayBuffer} Encrypted message buffer
 */
async function encryptByAes (message: ArrayBuffer, aesKey: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
  const aesAlgoEncrypt: AesCbcParams = {
    name: 'AES-CBC',
    iv,
  }

  return window.crypto.subtle.encrypt(aesAlgoEncrypt, aesKey, message)
}

/**
 * Convert browser-based CryptoKey to reusable set of bytes that can be used everywhere
 * @param {CryptoKey} aesKey
 * @returns {ArrayBuffer}
 */
async function exportAesKey (aesKey: CryptoKey): Promise<ArrayBuffer> {
  return window.crypto.subtle.exportKey('raw', aesKey)
}

// see also: https://stackoverflow.com/questions/9267899/arraybuffer-to-base64-encoded-string
function arrayBufferToBase64 (buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  const strBinary = bytes.reduce((result, byte) => result + String.fromCharCode(byte), '')
  return window.btoa(strBinary)
}

// see also: https://stackoverflow.com/questions/21797299/convert-base64-string-to-arraybuffer
function base64ToArrayBuffer (str: string): ArrayBuffer {
  const strBinary = window.atob(str)
  const len = strBinary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = strBinary.charCodeAt(i)
  }
  return bytes.buffer
}

// see also: https://gist.github.com/72lions/4528834
function concatBuffers (...buffers: ArrayBuffer[]): ArrayBuffer {
  return buffers.reduce<Uint8Array>((result, buffer) => {
    const nextResult = new Uint8Array(result.byteLength + buffer.byteLength)
    nextResult.set(new Uint8Array(result), 0)
    nextResult.set(new Uint8Array(buffer), result.byteLength)
    return nextResult
  }, new Uint8Array(0)).buffer
}

/**
 * Convert exported rsa public key to browser-based CryptoKey that browser can use for encrypting
 * @param {string} rsaPublicKey base64 string
 * @returns {CryptoKey}
 */
async function importRsaKey (rsaPublicKey: string): Promise<CryptoKey> {
  const bufferKey = base64ToArrayBuffer(rsaPublicKey)
  const rsaParams = {
    name: 'RSA-OAEP',
    hash: {
      name: 'SHA-1',
    },
  }
  return window.crypto.subtle.importKey('spki', bufferKey, rsaParams, false, ['encrypt'])
}

async function encryptByRsa (message: ArrayBuffer, rsaKey: CryptoKey): Promise<ArrayBuffer> {
  const rsaEncryptOptions = {
    name: 'RSA-OAEP',
    hash: 'SHA-1',
  }

  return window.crypto.subtle.encrypt(rsaEncryptOptions, rsaKey, message)
}

// rsa(aesKey)[256bytes] + iv[16bytes] + aes(message)
export async function encryptCredentials (message: string, base64RsaPublicKey: string): Promise<string> {
  if (!window.crypto || !window.crypto.subtle) {
    return Promise.resolve('')
  }

  // get things for symmetrical encryption
  const aesKey = await generateAesKey()
  const aesKeyBuffer = await exportAesKey(aesKey)
  const iv = generateAesIV()

  const publicRsaKey = await importRsaKey(base64RsaPublicKey)
  const encryptedAesKey = await encryptByRsa(aesKeyBuffer, publicRsaKey)

  const messageBuffer = stringToBuffer(message)
  const encryptedMessageBuffer = await encryptByAes(messageBuffer, aesKey, iv)

  const resultBuffer = concatBuffers(encryptedAesKey, iv.buffer, encryptedMessageBuffer)
  return arrayBufferToBase64(resultBuffer)
}
